/* eslint-disable react/no-unused-prop-types */
/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import TileLayer from 'ol/layer/Tile';
import { WMTS } from 'ol/source';
import type { Options as WMTSOptions } from 'ol/source/WMTS';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import {
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';
import type { Dimension } from '@opengeoweb/webmap';
import { LayerType } from '@opengeoweb/webmap';
import { WMTSDimensionsFromDimensions } from './utils';
import {
  useGetWMLayerInstance,
  useQueryWMTSGetCapabilities,
} from '../../../hooks/useHooksGetCapabilities';
import MapContext from '../context/MapContext';
import { getDimensionParamsForWMS } from '../utils/makeDimValuesToPrefetch';
import {
  selectPreferredWMTSTileMatrixSet,
  useProjection,
} from '../utils/projections';
import type { OnInitializeLayerProps } from '../utils/types';

export interface TimeAwareWMTSLayerProps {
  layerId?: string;
  getCapsURL: string;
  layerName: string;
  styleName?: string;
  layerOptions: ConstructorParameters<typeof TileLayer>[0];
  dimensions?: Dimension[];
  visible?: boolean;
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  onLayerReady?: (layerId: string) => void;
  onLayerError?: (layerId: string, message: string) => void;
  debugMode?: boolean;
}

export const TimeAwareWMTSLayer: React.FC<TimeAwareWMTSLayerProps> = ({
  getCapsURL,
  layerName,
  dimensions = [],
  layerOptions,
  layerId,
  onInitializeLayer,
  onLayerReady,
  onLayerError,
  visible = true,
}: TimeAwareWMTSLayerProps) => {
  const { map } = useContext(MapContext);
  const [layer, setLayer] = useState<TileLayer>();
  const [source, setSource] = useState<WMTS>();

  const projectionName = useProjection();

  // get a wmLayer instance
  const { wmLayer, errorMessage } = useGetWMLayerInstance({
    service: getCapsURL,
    name: layerName,
    id: layerId,
    layerType: LayerType.wmtsMapLayer,
  });

  // dimensionsValues is a key/value dictionary with dimension names specifically for WMS GetMap requests
  const dimensionsValues = getDimensionParamsForWMS(dimensions, wmLayer);

  // This string can be used safely in a useEffect or other React hook to check if the contents changed
  const dimensionsValuesEffectCheck =
    dimensionsValues &&
    (Object.entries(dimensionsValues)
      .map((dimensionNameAndCurrentValue) => {
        return `${dimensionNameAndCurrentValue[0]}:${dimensionNameAndCurrentValue[1]}`;
      })
      .join(',') as string);

  // Initialize layer for GeoWeb, call onInitializeLayer
  useEffect(() => {
    const initializeLayer = async (): Promise<void> => {
      if (wmLayer) {
        const wmTimeDim = wmLayer?.dimensions?.find(
          (dimension) => dimension.name === 'time',
        );
        // Set time dimension current value based on layerprops dimension value

        const layerPropsTimeDim = dimensions?.find(
          (dimension) => dimension.name === 'time',
        );

        const timeValueToInitialize =
          layerPropsTimeDim?.currentValue || wmTimeDim?.getValue();
        wmTimeDim &&
          timeValueToInitialize &&
          wmTimeDim.setValue(timeValueToInitialize);

        const refTimeDim = wmLayer?.dimensions?.find(
          (dimension) => dimension.name === 'reference_time',
        );

        // Update reference time dimension value, this will change the time values to prefetch. Depends on model run.
        if (refTimeDim && wmTimeDim) {
          const layerPropsRefTimeDim = dimensions?.find(
            (dimension) => dimension.name === 'reference_time',
          );
          // Set reftime dimension current value based on layerprops dimension value
          refTimeDim &&
            layerPropsRefTimeDim?.currentValue &&
            refTimeDim.setValue(layerPropsRefTimeDim?.currentValue);

          const refTimeToFind = wmTimeDim.getValue();
          const firstRefTime = refTimeDim?.getClosestValue(refTimeToFind, true);

          firstRefTime &&
            refTimeDim &&
            wmTimeDim?.setTimeValuesForReferenceTime(firstRefTime, refTimeDim);
        }

        // Make sure we have the latest dimension values before initializing
        await wmLayer.parseLayer();

        onInitializeLayer?.({
          layerId: wmLayer.id,
          style: wmLayer.currentStyle!,
          dimensions: wmLayer.dimensions.map((dimension) => {
            return {
              name: dimension.name,
              units: dimension.units,
              currentValue: dimension.currentValue,
              maxValue: dimension.getLastValue(),
              minValue: dimension.getFirstValue(),
              synced: dimension.synced,
              values: dimension.values,
              unitSymbol: dimension.unitSymbol,
            };
          }),
        });
      }
    };
    initializeLayer().catch((e) => onLayerError?.(layerId!, e.message));

    return (): void => {};

    // Effect array should only contain wmLayer and layerName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wmLayer, layerName]);

  const {
    data: wmtsCaps,
    isLoading,
    isError,
    error,
  } = useQueryWMTSGetCapabilities(getCapsURL);

  const { options: wmtsOptions, error: parsingError } = useMemo((): {
    options: WMTSOptions | null;
    error?: string;
  } => {
    if (isLoading || isError || !wmtsCaps || !projectionName) {
      return { options: null };
    }

    try {
      const capabilities = new WMTSCapabilities().read(wmtsCaps as string);
      const matrixSet = selectPreferredWMTSTileMatrixSet(
        capabilities,
        layerName,
        projectionName,
      );
      if (!matrixSet) {
        return { options: null };
      }
      return {
        options: optionsFromCapabilities(capabilities, {
          layer: layerName,
          matrixSet,
        }),
      };
    } catch (e) {
      return {
        options: null,
        error: `Error parsing WMTS capabilities: ${e instanceof Error ? e.message : String(e)}`,
      };
    }
  }, [wmtsCaps, isLoading, isError, layerName, projectionName]);

  const layerError =
    errorMessage ||
    (isError &&
      `Error fetching WMTS capabilities: ${error?.message || 'Unknown error'}`) ||
    parsingError;
  const isLayerReady = !!wmLayer && !!wmtsOptions;
  const reportLayerStatus = useEffectEvent(() => {
    if (layerError) {
      onLayerError?.(layerId || '', layerError);
    } else if (isLayerReady && layerId) {
      onLayerReady?.(layerId);
    }
  });

  useEffect(() => {
    reportLayerStatus();
  }, [layerError, isLayerReady, layerId]);

  useEffect(() => {
    if (!map || !wmtsOptions) {
      return (): void => {};
    }
    const source = new WMTS({
      ...wmtsOptions,
      ...(dimensions
        ? {
            dimensions: WMTSDimensionsFromDimensions(dimensions),
          }
        : {}),
      crossOrigin: 'anonymous',
    });

    const layer = new TileLayer({ source });

    map.addLayer(layer);
    setLayer(layer);
    setSource(source);
    if (wmLayer) {
      wmLayer.olSource = source;
    }

    return (): void => {
      map.removeLayer(layer);
      setLayer(undefined);
      setSource(undefined);
      if (wmLayer) {
        wmLayer.olSource = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, wmtsOptions, wmLayer]);

  useEffect(() => {
    if (dimensions) {
      source?.updateDimensions(WMTSDimensionsFromDimensions(dimensions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsValuesEffectCheck]);

  useEffect(() => {
    if (!layerOptions || !layer) {
      return;
    }

    if (layerOptions.opacity !== undefined && layerOptions.opacity !== null) {
      layer.setOpacity(layerOptions.opacity);
    }
    if (layerOptions.zIndex !== undefined && layerOptions.zIndex !== null) {
      layer.setZIndex(layerOptions.zIndex);
    }
  }, [layer, layerOptions]);

  useEffect(() => {
    if (!visible || !layer) {
      return;
    }
    layer.setVisible(!!visible);
  }, [layer, visible]);

  return null;
};
